import React, { useEffect, useMemo, useState } from 'react'
import { Link } from 'react-router-dom'
import { v4 as uuidv4 } from 'uuid'

import Page from '../page'
import { NavInExperience } from '../../components/nav'
import { experiences as experiencesApi } from '../../lib/api'
import { notifications } from '../../stores'
import { t } from '../../stores/i18n.store'
import { buildSwitchOverview } from '../../stores/switchOverview'
import { analyzeSwitches, inferType, validateRegistry } from '../../stores/switchInsights'

const METHOD_LABELS = {
  equal: { symbol: '=', text: 'est égal à' },
  contain: { symbol: '∈', text: 'contient' },
  exist: { symbol: '∃', text: 'existe' },
  greater: { symbol: '>', text: 'est supérieur à' },
  less: { symbol: '<', text: 'est inférieur à' }
}

const ALERTS = {
  'read-never-written': { label: 'Testé mais jamais posé', sev: 'danger' },
  'value-not-in-enum': { label: 'Valeur hors des valeurs autorisées', sev: 'danger' },
  'not-numeric': { label: 'Valeur non numérique', sev: 'danger' },
  'near-duplicate': { label: 'Clé proche d’une autre', sev: 'danger' },
  'written-never-read': { label: 'Posé mais jamais testé', sev: 'warning' },
  'value-never-written': { label: 'Teste une valeur jamais posée', sev: 'warning' },
  unregistered: { label: 'Non déclaré au registre', sev: 'info' }
}
const SEV = {
  danger: { color: '#a94442', bg: '#f2dede', border: '#ebccd1' },
  warning: { color: '#8a6d3b', bg: '#fcf8e3', border: '#faebcc' },
  info: { color: '#31708f', bg: '#d9edf7', border: '#bce8f1' }
}

const TYPE_OPTIONS = [
  { value: 'string', label: 'Texte libre' },
  { value: 'number', label: 'Nombre' },
  { value: 'enum', label: 'Valeurs exactes' }
]
const TYPE_CHIP = {
  string: { label: 'texte', color: '#6b7280' },
  number: { label: 'nombre', color: '#3f7fd8' },
  enum: { label: 'valeurs exactes', color: '#8e44ad' }
}

export const SwitchManager = props => {
  const { experienceUid } = props.match.params
  const [tab, setTab] = useState('overview')
  const [experience, setExperience] = useState(undefined)
  const [registry, setRegistry] = useState([])
  const [overview, setOverview] = useState(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)

  const savedRegistry = experience?.switchRegistry || []
  const needSave = JSON.stringify(registry) !== JSON.stringify(savedRegistry)

  useEffect(() => {
    experiencesApi.get(experienceUid).then(exp => {
      setExperience(exp)
      setRegistry(exp?.switchRegistry ? JSON.parse(JSON.stringify(exp.switchRegistry)) : [])
    })
  }, [experienceUid])

  const loadOverview = (noCache = false) => {
    setLoading(true)
    setError(null)
    buildSwitchOverview(experienceUid, noCache)
      .then(setOverview)
      .catch(err => setError(err?.toString() || 'Erreur inconnue'))
      .finally(() => setLoading(false))
  }
  useEffect(() => {
    loadOverview()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [experienceUid])

  useEffect(() => {
    const onExit = event => needSave && (event.returnValue = t('unsaved'))
    const unblock = props.history.block(() => {
      if (needSave) return t('unsaved')
    })
    window.addEventListener('beforeunload', onExit)
    return () => void (window.removeEventListener('beforeunload', onExit), unblock())
  }, [needSave, props.history])

  const handleSave = () => {
    const id = Date.now()
    experiencesApi
      .update(experienceUid, { _id: experienceUid, switchRegistry: registry })
      .then(() => {
        setExperience(prev => ({ ...prev, switchRegistry: registry }))
        notifications.set(id, { id, type: 'success', title: t('save-success'), autoClose: 1500 })
      })
      .catch(err => notifications.set(id, { id, type: 'danger', title: t('save-error'), content: err.toString(), autoClose: 2500 }))
  }

  // Analyse enrichie (registre + fautes de frappe).
  const analyzed = useMemo(() => analyzeSwitches(overview?.switches || [], registry), [overview, registry])

  const addToRegistry = sw => {
    if (registry.some(e => e.key === sw.key)) return setTab('registry')
    setRegistry([...registry, { _id: uuidv4(), key: sw.key, ...inferType(sw.writtenValues) }])
    setTab('registry')
  }

  return (
    <Page>
      <NavInExperience />
      <div id="content">
        <div style={{ display: 'flex', alignItems: 'center', marginBottom: 15 }}>
          <h3 style={{ margin: 0, flex: 1 }}>Gestionnaire de switch</h3>
          {tab === 'overview' && (
            <button className="btn btn-default" onClick={() => loadOverview(true)} disabled={loading}>
              <i className={`fa fa-refresh ${loading ? 'fa-spin' : ''}`} style={{ marginRight: 8 }} />
              Rafraîchir
            </button>
          )}
        </div>

        <ul className="nav nav-tabs" style={{ marginBottom: 20 }}>
          <li className={tab === 'overview' ? 'active' : ''}>
            <a onClick={() => setTab('overview')} style={{ cursor: 'pointer' }}>Vue d'ensemble</a>
          </li>
          <li className={tab === 'registry' ? 'active' : ''}>
            <a onClick={() => setTab('registry')} style={{ cursor: 'pointer' }}>
              Registre
              {registry.length > 0 && (
                <span
                  style={{
                    display: 'inline-block',
                    minWidth: 18,
                    height: 18,
                    padding: '0 6px',
                    marginLeft: 6,
                    borderRadius: 9,
                    background: '#dfe3e8',
                    color: '#555',
                    fontSize: 11,
                    lineHeight: '18px',
                    textAlign: 'center',
                    verticalAlign: 'middle'
                  }}
                >
                  {registry.length}
                </span>
              )}
            </a>
          </li>
        </ul>

        {tab === 'overview' ? (
          <OverviewTab analyzed={analyzed} loading={loading} error={error} hasOverview={!!overview} onAddToRegistry={addToRegistry} />
        ) : (
          <RegistryTab registry={registry} setRegistry={setRegistry} overview={overview} needSave={needSave} onSave={handleSave} />
        )}
      </div>
      <div id="app-preview" />
    </Page>
  )
}

/* ------------------------------------------------------------------ */
/* Onglet « Vue d'ensemble »                                           */
/* ------------------------------------------------------------------ */

const OverviewTab = ({ analyzed, loading, error, hasOverview, onAddToRegistry }) => {
  const [search, setSearch] = useState('')
  const [filter, setFilter] = useState('all')
  const [expanded, setExpanded] = useState({})

  const counts = useMemo(
    () => ({
      total: analyzed.length,
      'written-never-read': analyzed.filter(s => s.warnings.includes('written-never-read')).length,
      'read-never-written': analyzed.filter(s => s.warnings.includes('read-never-written')).length,
      unregistered: analyzed.filter(s => s.warnings.includes('unregistered')).length,
      'near-duplicate': analyzed.filter(s => s.warnings.includes('near-duplicate')).length
    }),
    [analyzed]
  )

  const visible = useMemo(() => {
    const term = search.trim().toLowerCase()
    return analyzed.filter(s => {
      if (filter !== 'all' && !s.warnings.includes(filter)) return false
      if (!term) return true
      const hay = [s.key, s.entry?.name, ...s.writtenValues, ...s.testedValues, ...s.writes.map(w => w.source.label), ...s.reads.map(r => r.source.label)]
        .join(' ')
        .toLowerCase()
      return hay.includes(term)
    })
  }, [analyzed, search, filter])

  const toggle = key => setExpanded(prev => ({ ...prev, [key]: !prev[key] }))

  if (loading && !hasOverview) {
    return (
      <p className="bg-info" style={{ padding: 12 }}>
        <i className="fa fa-cog fa-spin fa-fw" style={{ marginRight: 8 }} />
        Analyse de l'expérience en cours…
      </p>
    )
  }
  if (error) return <p className="bg-danger" style={{ padding: 12 }}>Impossible d'analyser les switches : {error}</p>
  if (!hasOverview) return null

  return (
    <>
      <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap', marginBottom: 16 }}>
        <MetricCard label="switches" value={counts.total} active={filter === 'all'} onClick={() => setFilter('all')} />
        <MetricCard label="posés jamais testés" value={counts['written-never-read']} tone="warning" active={filter === 'written-never-read'} onClick={() => setFilter('written-never-read')} />
        <MetricCard label="testés jamais posés" value={counts['read-never-written']} tone="danger" active={filter === 'read-never-written'} onClick={() => setFilter('read-never-written')} />
        <MetricCard label="non déclarés" value={counts.unregistered} tone="info" active={filter === 'unregistered'} onClick={() => setFilter('unregistered')} />
        <MetricCard label="clés proches" value={counts['near-duplicate']} tone="danger" active={filter === 'near-duplicate'} onClick={() => setFilter('near-duplicate')} />
      </div>

      <div className="form-group" style={{ maxWidth: 420 }}>
        <input type="text" className="form-control" placeholder="Rechercher une clé, un nom, une valeur, un emplacement…" value={search} onChange={e => setSearch(e.target.value)} />
      </div>

      {analyzed.length === 0 ? (
        <p className="bg-info" style={{ padding: 12 }}>Aucun switch n'est utilisé dans cette expérience.</p>
      ) : visible.length === 0 ? (
        <p className="text-muted">Aucun switch ne correspond au filtre.</p>
      ) : (
        <table className="table" style={{ backgroundColor: '#fff' }}>
          <thead>
            <tr style={{ color: '#888', fontSize: 12 }}>
              <th style={{ width: 28 }} />
              <th>Switch</th>
              <th>Valeurs posées</th>
              <th style={{ width: 120 }}>Usage</th>
              <th style={{ width: 260 }}>Alertes</th>
            </tr>
          </thead>
          <tbody>
            {visible.map(s => (
              <SwitchRow key={s.key} data={s} expanded={!!expanded[s.key]} onToggle={() => toggle(s.key)} onAddToRegistry={onAddToRegistry} />
            ))}
          </tbody>
        </table>
      )}
    </>
  )
}

const MetricCard = ({ label, value, tone, active, onClick }) => {
  const s = tone ? SEV[tone] : { color: '#333', bg: '#f5f5f5', border: '#e3e3e3' }
  const dim = value === 0 && tone
  return (
    <button
      onClick={onClick}
      style={{
        border: `1px solid ${active ? s.color : s.border}`,
        background: active ? s.bg : '#fff',
        borderRadius: 6,
        padding: '8px 14px',
        minWidth: 110,
        textAlign: 'left',
        cursor: 'pointer',
        opacity: dim ? 0.55 : 1
      }}
    >
      <div style={{ fontSize: 22, fontWeight: 500, color: s.color, lineHeight: 1.1 }}>{value}</div>
      <div style={{ fontSize: 12, color: '#888' }}>{label}</div>
    </button>
  )
}

const SwitchRow = ({ data, expanded, onToggle, onAddToRegistry }) => (
  <>
    <tr onClick={onToggle} style={{ cursor: 'pointer' }}>
      <td style={{ textAlign: 'center', color: '#aaa' }}>
        <i className={`fa fa-caret-${expanded ? 'down' : 'right'}`} />
      </td>
      <td>
        <code style={{ fontSize: 13 }}>{data.key}</code>
        {data.entry ? (
          <TypeChip type={data.entry.type} />
        ) : (
          <span style={{ marginLeft: 8, fontSize: 11, color: '#999' }}>non déclaré</span>
        )}
      </td>
      <td>
        {data.writtenValues.length === 0 ? (
          <span className="text-muted">—</span>
        ) : (
          data.writtenValues.map(v => (
            <span key={v} style={{ ...chipStyle, marginRight: 4 }}>{v === '' ? '(vide)' : v}</span>
          ))
        )}
      </td>
      <td style={{ whiteSpace: 'nowrap', color: '#555', fontSize: 13 }}>
        <span title="Actions qui posent/suppriment"><i className="fa fa-pencil" style={{ color: '#888', marginRight: 4 }} />{data.writes.length}</span>
        <span style={{ margin: '0 6px', color: '#ccc' }}>·</span>
        <span title="Conditions qui testent"><i className="fa fa-eye" style={{ color: '#888', marginRight: 4 }} />{data.reads.length}</span>
      </td>
      <td>
        {data.warnings.length === 0 ? (
          <span style={{ color: '#3c763d', fontSize: 12 }}><i className="fa fa-check" style={{ marginRight: 4 }} />OK</span>
        ) : (
          data.warnings.map(w => <AlertBadge key={w} warning={w} near={data.nearDuplicates} />)
        )}
      </td>
    </tr>
    {expanded && (
      <tr>
        <td />
        <td colSpan={4} style={{ backgroundColor: '#fafafa' }}>
          {!data.entry && (
            <div style={{ marginBottom: 10 }}>
              <button className="btn btn-xs btn-default" onClick={() => onAddToRegistry(data)}>
                <i className="fa fa-plus" style={{ marginRight: 5 }} />Ajouter au registre
              </button>
            </div>
          )}
          <div style={{ display: 'flex', gap: 16, flexWrap: 'wrap', padding: '4px 0' }}>
            <SourceCard
              icon="pencil"
              title={`Écrit par (${data.writes.length})`}
              emptyText="Aucune action n'écrit ce switch."
              items={data.writes.map((w, i) => ({
                key: i,
                path: w.source.path,
                label: w.source.label,
                detail: w.actionType === 'delete_switch' ? 'supprime la clé' : `pose « ${w.value === '' ? '(vide)' : w.value} »`
              }))}
            />
            <SourceCard
              icon="eye"
              title={`Testé par (${data.reads.length})`}
              emptyText="Aucun module Switch ne teste ce switch."
              items={data.reads.map((r, i) => {
                const m = METHOD_LABELS[r.method] || { symbol: '?', text: r.method }
                const val = r.method === 'exist' ? '' : ` « ${r.value == null || r.value === '' ? '(vide)' : r.value} »`
                return { key: i, path: r.source.path, label: r.source.label, detail: `${m.symbol} ${m.text}${val}` }
              })}
            />
          </div>
        </td>
      </tr>
    )}
  </>
)

const TypeChip = ({ type }) => {
  const c = TYPE_CHIP[type] || TYPE_CHIP.string
  return (
    <span style={{ marginLeft: 8, fontSize: 11, color: c.color, border: `1px solid ${c.color}`, borderRadius: 10, padding: '0 7px' }}>{c.label}</span>
  )
}

const AlertBadge = ({ warning, near }) => {
  const a = ALERTS[warning]
  if (!a) return null
  const s = SEV[a.sev]
  const label = warning === 'near-duplicate' && near?.length ? `Proche de « ${near.join(' », « ')} »` : a.label
  return (
    <span style={{ display: 'inline-block', color: s.color, background: s.bg, border: `1px solid ${s.border}`, borderRadius: 4, fontSize: 11, padding: '1px 7px', marginRight: 4, marginBottom: 3 }}>
      <i className="fa fa-exclamation-triangle" style={{ marginRight: 4 }} />{label}
    </span>
  )
}

const chipStyle = { display: 'inline-block', background: '#eef1f4', border: '1px solid #dfe3e8', borderRadius: 4, fontSize: 12, padding: '0 7px', color: '#445' }

const SourceCard = ({ icon, title, items, emptyText }) => (
  <div style={{ flex: 1, minWidth: 300, background: '#fff', border: '1px solid #eee', borderRadius: 6, padding: '10px 12px' }}>
    <div style={{ fontWeight: 500, marginBottom: 8, fontSize: 13 }}>
      <i className={`fa fa-${icon}`} style={{ color: '#999', marginRight: 6 }} />{title}
    </div>
    {items.length === 0 ? (
      <div className="text-muted" style={{ fontSize: 13 }}>{emptyText}</div>
    ) : (
      <ul style={{ listStyle: 'none', paddingLeft: 0, margin: 0 }}>
        {items.map(item => (
          <li key={item.key} style={{ marginBottom: 5, fontSize: 13 }}>
            <Link to={item.path}>{item.label}</Link>
            <span className="text-muted" style={{ marginLeft: 6, fontSize: 12 }}>{item.detail}</span>
          </li>
        ))}
      </ul>
    )}
  </div>
)

/* ------------------------------------------------------------------ */
/* Onglet « Registre »                                                 */
/* ------------------------------------------------------------------ */

const RegistryTab = ({ registry, setRegistry, overview, needSave, onSave }) => {
  const issuesByIndex = useMemo(() => validateRegistry(registry), [registry])
  const hasIssues = issuesByIndex.some(i => i.length > 0)

  const updateEntry = (index, patch) => {
    const next = [...registry]
    next[index] = { ...next[index], ...patch }
    setRegistry(next)
  }
  const deleteEntry = index => {
    const next = [...registry]
    next.splice(index, 1)
    setRegistry(next)
  }
  const addEntry = () => setRegistry([...registry, { _id: uuidv4(), key: '', type: 'string', values: [] }])
  const importDetected = () => {
    const existing = new Set(registry.map(e => e.key))
    const toAdd = (overview?.switches || [])
      .filter(s => s.key && !existing.has(s.key))
      .map(s => ({ _id: uuidv4(), key: s.key, ...inferType(s.writtenValues) }))
    if (toAdd.length) setRegistry([...registry, ...toAdd])
  }
  const detectedCount = (overview?.switches || []).filter(s => s.key && !registry.some(e => e.key === s.key)).length

  return (
    <>
      <p className="text-muted">
        Déclarez les switches et leurs valeurs possibles pour les réutiliser sans faute de frappe dans les actions et
        conditions. Ce qui est saisi ici est exactement ce qui est enregistré en JSON.
      </p>

      <div style={{ marginBottom: 15 }}>
        <button onClick={addEntry} className="btn btn-primary" style={{ marginRight: 8 }}>
          <i className="fa fa-plus" style={{ marginRight: 8 }} />Ajouter un switch
        </button>
        <button onClick={importDetected} className="btn btn-default" disabled={detectedCount === 0}>
          <i className="fa fa-magic" style={{ marginRight: 8 }} />
          Importer les clés détectées{detectedCount > 0 ? ` (${detectedCount})` : ''}
        </button>
      </div>

      {registry.length === 0 ? (
        <p className="bg-info" style={{ padding: 12 }}>Aucun switch nommé pour l'instant.</p>
      ) : (
        registry.map((entry, index) => (
          <RegistryEntry
            key={entry._id || index}
            entry={entry}
            issues={issuesByIndex[index]}
            onChange={patch => updateEntry(index, patch)}
            onDelete={() => deleteEntry(index)}
          />
        ))
      )}

      <div style={{ position: 'fixed', bottom: 5, right: 5, ...(!needSave && { opacity: 0.6, pointerEvents: 'none' }) }}>
        {hasIssues && <span style={{ marginRight: 10, color: '#a94442', fontSize: 12 }}><i className="fa fa-exclamation-triangle" /> corrigez les erreurs</span>}
        <button onClick={onSave} className="btn btn-primary" disabled={hasIssues}>Enregistrer</button>
      </div>
    </>
  )
}

const RegistryEntry = ({ entry, issues = [], onChange, onDelete }) => {
  const values = entry.values || []
  const dup = issues.includes('duplicate-key')
  const empty = issues.includes('empty-key')
  const keyError = dup || empty

  const updateValue = (i, next) => {
    const list = [...values]
    list[i] = next
    onChange({ values: list })
  }
  const deleteValue = i => {
    const list = [...values]
    list.splice(i, 1)
    onChange({ values: list })
  }
  const addValue = () => onChange({ values: [...values, ''] })

  const chip = TYPE_CHIP[entry.type] || TYPE_CHIP.string

  return (
    <div style={{ border: '1px solid #e3e3e3', backgroundColor: '#fff', borderRadius: 8, padding: 15, marginBottom: 12, borderLeft: `4px solid ${chip.color}` }}>
      <div style={{ display: 'flex', gap: 12, alignItems: 'flex-end', flexWrap: 'wrap' }}>
        <div className="form-group" style={{ flex: 1, minWidth: 200, margin: 0 }}>
          <label>Clé</label>
          <input
            type="text"
            className="form-control"
            style={keyError ? { borderColor: '#a94442' } : undefined}
            value={entry.key || ''}
            onChange={e => onChange({ key: e.target.value })}
          />
          {dup && <div style={{ color: '#a94442', fontSize: 12, marginTop: 3 }}>Cette clé est en double.</div>}
          {empty && <div style={{ color: '#a94442', fontSize: 12, marginTop: 3 }}>La clé ne peut pas être vide.</div>}
        </div>
        <div className="form-group" style={{ width: 170, margin: 0 }}>
          <label>Type de valeur</label>
          <select className="form-control" value={entry.type || 'string'} onChange={e => onChange({ type: e.target.value })}>
            {TYPE_OPTIONS.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
          </select>
        </div>
        <button onClick={onDelete} className="btn btn-danger" style={{ marginBottom: 0 }}>
          <i className="fa fa-trash" />
        </button>
      </div>

      {entry.type === 'enum' && (
        <div style={{ marginTop: 12, paddingLeft: 12, borderLeft: '3px solid #eee' }}>
          <label>Valeurs possibles</label>
          {values.length === 0 && <div className="text-muted" style={{ fontSize: 12, marginBottom: 4 }}>Ajoutez au moins une valeur.</div>}
          {values.map((v, i) => (
            <div key={i} style={{ display: 'flex', gap: 8, marginBottom: 6, alignItems: 'center', maxWidth: 400 }}>
              <input type="text" className="form-control" placeholder="Valeur enregistrée en JSON" value={v || ''} onChange={e => updateValue(i, e.target.value)} />
              <button onClick={() => deleteValue(i)} className="btn btn-danger btn-sm"><i className="fa fa-trash" /></button>
            </div>
          ))}
          <button onClick={addValue} className="btn btn-default btn-sm">
            <i className="fa fa-plus" style={{ marginRight: 6 }} />Ajouter une valeur
          </button>
        </div>
      )}
    </div>
  )
}
